import React, { useCallback, useMemo, useState } from 'react'

import { twMerge } from 'tailwind-merge'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/common/AlertDialog'
import Button from '@/components/common/Button'
import { getEntityTypeLabel } from '@/components/library-v2/ui/Entity'

import { isPromiseLike } from '@/helpers/promise'

import { useDeleteCompanies } from '@/hooks/library/useDeleteCompanies'
import { useDeletePeople } from '@/hooks/library/useDeletePeople'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type { Entity } from '@/types/library/Entity'

const waitForNextPaint = async (): Promise<void> => {
  if (typeof window === 'undefined') {
    return
  }

  await new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => {
      resolve()
    })
  })
}

interface DeleteDialogProps {
  label: string
  open: boolean
  setOpen: (isOpen: boolean) => void
  onDelete: () => void | Promise<void>
  children?:
    | React.ReactNode
    | ((state: { isDeleting: boolean }) => React.ReactNode)
  disabled?: boolean
  className?: string
}

export const DeleteDialog: React.FC<DeleteDialogProps> = ({
  label,
  open,
  setOpen,
  onDelete,
  children,
  disabled = false,
  className,
}) => {
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const handleDelete = useCallback(async () => {
    try {
      setIsDeleting(true)
      await waitForNextPaint()

      const result = onDelete()
      if (isPromiseLike(result)) {
        await result
      }
      setOpen(false)
    } catch (error) {
      console.error(error)
    } finally {
      setIsDeleting(false)
    }
  }, [onDelete, setOpen])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (isDeleting) {
        return
      }
      setOpen(nextOpen)
    },
    [isDeleting, setOpen],
  )

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className={twMerge('sm:max-w-[412px]', className)}>
        <div className="space-y-3">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {typeof children === 'function' ? children({ isDeleting }) : children}
          <AlertDialogFooter>
            <AlertDialogCancel
              variant="outline-compact"
              onClick={handleClose}
              disabled={isDeleting}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="danger-compact"
              type="button"
              onClick={handleDelete}
              disabled={disabled || isDeleting}
              isLoading={isDeleting}
            >
              <span>Delete {label}</span>
            </Button>
          </AlertDialogFooter>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}

interface DeleteEntitiesDialogProps {
  entities: Entity[]
  open: boolean
  setOpen: (open: boolean) => void
  onSuccess?: () => void
}

export const DeleteEntitiesDialog: React.FC<DeleteEntitiesDialogProps> = ({
  entities,
  open,
  setOpen,
  onSuccess,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { mutate: deletePeople, isPending: isDeletingPeople } = useDeletePeople(
    { orgId: activeOrgId },
  )
  const { mutate: deleteCompanies, isPending: isDeletingCompanies } =
    useDeleteCompanies({ orgId: activeOrgId })

  const { single, plural } = getEntityTypeLabel(entities[0])
  const title = `${entities.length.toLocaleString()} ${entities.length === 1 ? single : plural}`

  const isDeleting = isDeletingPeople || isDeletingCompanies

  const handleDelete = useCallback(async () => {
    try {
      const companies = entities.filter(
        (entity) => entity.entity_type === 'company',
      )
      const people = entities.filter(
        (entity) => entity.entity_type === 'person',
      )
      await Promise.all([
        people.length > 0
          ? new Promise<void>((resolve, reject) => {
              deletePeople(
                { ids: people.map((person) => person.id) },
                {
                  onSuccess: resolve,
                  onError: reject,
                },
              )
            })
          : Promise.resolve(),
        companies.length > 0
          ? new Promise<void>((resolve, reject) => {
              deleteCompanies(
                { ids: companies.map((company) => company.id) },
                {
                  onSuccess: resolve,
                  onError: reject,
                },
              )
            })
          : Promise.resolve(),
      ])
      setOpen(false)
      onSuccess?.()
    } catch (error) {
      console.error(error)
    }
  }, [entities, deletePeople, deleteCompanies, setOpen, onSuccess])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="w-[456px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {title}?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete{' '}
            {entities.length === 1 ? `this ${single}` : `these ${plural}`}? This
            action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex items-center justify-between sm:justify-between gap-2">
          <AlertDialogCancel variant="outline-compact" onClick={handleClose}>
            Cancel
          </AlertDialogCancel>
          <Button
            variant="danger-compact"
            onClick={handleDelete}
            disabled={entities.length === 0 || isDeleting}
            isLoading={isDeleting}
          >
            <span>Delete {title}</span>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
